import React, { useEffect, useState, useCallback, useRef } from 'react';

import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { Link, useLocation } from 'react-router-dom';
import { useTeamspace } from '../../context/TeamspaceProvider';
import { useAuth } from '../../context/AuthContext';
import { useColorMode } from '../../context/ColorModeContext';
import { useInvoicingBadge } from '../../context/InvoicingBadgeContext';
import { canAccessInvoicing } from '../../config/invoicingAccess';
import { isRestrictedStaff } from '../../config/staffRestrictions';
import { SALES_DASHBOARD_ENABLED_REGIONS } from '../../config/salesDashboard';

import ResizeHandle from './ResizeHandle';

const MOBILE_DRAWER_WIDTH = 220; // fits "Tracking Summary" with room to spare; 280 left a wide empty strip

const Sidebar = ({ 
  mobileOpen, 
  handleDrawerToggle,
  width = 240, 
  onWidthChange,
  isMobile
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const [openState, setOpenState] = useState({});
  const [lastExpandedWidth, setLastExpandedWidth] = useState(Math.max(180, width));
  const { teamspaces, loading, selectedTeamspaceId, setSelectedTeamspaceId, teamspaceIdToNameMap } = useTeamspace();
  const { logoutUser, user, role_dict } = useAuth();
  const { mode, toggleColorMode } = useColorMode();
  const { hasActiveInvoices } = useInvoicingBadge();
  // Active invoices only — the unbilled-items half of this dot was removed.
  const showInvoicingDot = hasActiveInvoices;
  const theme = useTheme();
  const location = useLocation();
  // Returns true when the current URL's page segment matches (handles sub-routes like /projects/123)
  const isActive = (segment) => {
    const page = location.pathname.split('/').slice(2).join('/');
    return page === segment || page.startsWith(segment + '/');
  };
  // Mobile (below md, `isMobile` from App.js): touch-sized rows and readable text.
  // Desktop keeps 40px / 0.75rem / medium icons.
  const itemMinHeight = isMobile ? 44 : 40;
  const headerHeight = 48; // desktop logo row; the mobile drawer has its own
  const listItemTypography = { fontSize: isMobile ? '0.875rem' : '0.75rem' };
  const iconFontSize = isMobile ? 'small' : 'medium';
  // The mobile drawer ignores the desktop resize width (which can be 60px, icons
  // only) and always opens at a fixed, text-showing width.
  const drawerWidth = isMobile ? MOBILE_DRAWER_WIDTH : width;
  // dvh: in mobile browsers 100vh is taller than the visible area, pushing the
  // bottom actions (Profile / Logout) under the browser's toolbar.
  const drawerHeight = isMobile ? '100dvh' : '100vh';

  const selectedTeamspace = teamspaces.find(ts => ts.id === selectedTeamspaceId);
  const teamspaceDisplayName = selectedTeamspace ? selectedTeamspace.name : "Teamspaces";

  const currentTeamspaceName = teamspaceIdToNameMap?.[selectedTeamspaceId];
  const isDeveloper = user && currentTeamspaceName && role_dict?.[currentTeamspaceName]?.role?.includes('Developer');
  // Invoicing / Quotation link visibility — same helper the two pages gate on.
  const canSeeInvoicing = canAccessInvoicing(user, role_dict, currentTeamspaceName);
  // Restricted Staff (DCC, PH) have no Tracking Summary — hide the link as well as blocking the page.
  const restrictedStaff = isRestrictedStaff(user, role_dict, currentTeamspaceName);

  const handleMenuClick = (itemId) => {
    setOpenState((prevOpenState) => ({
      ...prevOpenState,
      [itemId]: !prevOpenState[itemId],
    }));
  };

  const handleMouseDown = useCallback((e) => {
    if (isMobile) return; // Don't allow resizing on mobile
    
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e) => {
      // Change minimum width from 200px to 60px for icon-only mode
      const newWidth = Math.max(60, Math.min(600, startWidth + (e.clientX - startX)));
      onWidthChange(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, onWidthChange, isMobile]);

  // Collapse / expand handling for desktop
  const isCollapsed = !isMobile && width <= 60;
  const handleCollapseToggle = () => {
    if (isCollapsed) {
      onWidthChange(Math.max(180, lastExpandedWidth));
    } else {
      setLastExpandedWidth(width);
      onWidthChange(60);
    }
  };

  // The sidebar deliberately has NO loading early-return.
  //
  // It used to swap its whole body for a "Loading..." label whenever
  // TeamspaceProvider's `loading` flipped. That flips on every navigation
  // (`location.pathname` is in the provider's fetch dep array) and on every
  // auth/preferences refresh, so the entire nav blanked and rebuilt constantly.
  //
  // It never needed to: a refetch does NOT clear `teamspaces`,
  // `selectedTeamspaceId` or `teamspaceIdToNameMap` (the provider only empties
  // those on logout or error), and the nav itself is a static list rather than
  // something derived from the fetched data. So the previous menu stays correct
  // for the whole refresh. We keep rendering it and show a 2px progress bar
  // instead — the same background-refresh convention as the rest of the app.
  //
  // `teamspaceReady` covers the one genuine gap: a cold start, before any
  // teamspace id exists, when the links would point at /undefined/...

  const teamspaceReady = Boolean(selectedTeamspaceId);

  // Determine if we should show text based on width
  const showText = drawerWidth > 110; // Hide text a bit earlier for a sleeker look

  // On mobile the drawer is opened from the Header's menu button; the floating
  // mid-left toggle that used to sit over page content is gone.
  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          width: isMobile ? drawerWidth : 0, // On desktop, container takes no width; paper is fixed
          flexShrink: 0,
          whiteSpace: 'nowrap',
          transition: isResizing ? 'none' : theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          boxSizing: 'border-box',
          ...( (isMobile && !mobileOpen) && {
            width: 0,
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }),
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            ...(isMobile && { maxWidth: '85vw' }),
            boxSizing: 'border-box',
            position: 'fixed',
            top: 0,
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            height: drawerHeight,
            minHeight: 0,
            ...( (isMobile && !mobileOpen) && {
              width: 0,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            }),
          },
        }}
        ref={sidebarRef}
      >
        {/* Absolutely positioned so showing/hiding it cannot shift any content. */}
        {loading && (
          <LinearProgress
            color="info"
            aria-label="Refreshing teamspaces"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              zIndex: 2,
              backgroundColor: 'transparent',
            }}
          />
        )}
        <Box sx={{ overflowX: 'hidden', display: 'flex', flexDirection: 'column', height: drawerHeight, minHeight: 0 }}>
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 56, px: 1.5, flexShrink: 0 }}>
              <Box
                component="img"
                src="/gva_logo_v2.png"
                alt="GVA Logo"
                sx={{ height: '2rem', width: 'auto', objectFit: 'contain' }}
              />
              <IconButton
                aria-label="Close menu"
                onClick={handleDrawerToggle}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>
          )}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: headerHeight, px: 1 }}>
              <Box sx={{ display: isCollapsed ? 'none' : 'flex', alignItems: 'center', gap: 0 }}>
                <Box
                  component="img"
                  src="/gva_logo_v2.png"
                  alt="GVA Logo"
                  sx={{
                    height: showText ? '2rem' : '1.2rem',
                    width: 'auto',
                    objectFit: 'contain',
                    opacity: showText ? 1 : 0.7,
                    transition: 'all 0.2s ease-in-out',
                    maxHeight: headerHeight * 0.6,
                  }}
                />
              </Box>
              <IconButton
                aria-label={isCollapsed ? 'expand sidebar' : 'collapse sidebar'}
                onClick={handleCollapseToggle}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                }}
              >
                {theme.direction === 'rtl' ? (
                  isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />
                ) : (
                  isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />
                )}
              </IconButton>
            </Box>
          )}
          <Box
            aria-busy={loading || undefined}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              // Cold start only: the links would resolve to /undefined/... until
              // a teamspace id exists. Dim and disarm them in place rather than
              // unmounting the menu, so nothing moves when the id lands.
              opacity: teamspaceReady ? 1 : 0.5,
              pointerEvents: teamspaceReady ? 'auto' : 'none',
              transition: 'opacity 150ms ease',
            }}
          >
            <List component="nav" dense>

          {/* <ListItemButton component={Link} to={`/${selectedTeamspaceId}/time_entries`}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              {showText && <ListItemText primary="Home" />}
            </ListItemButton> */}

            

            {!restrictedStaff && (
              <ListItemButton component={Link} to={`/${selectedTeamspaceId}/tracking-summary`} selected={isActive('tracking-summary')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <DashboardIcon fontSize={iconFontSize} />
                </ListItemIcon>
                {showText && <ListItemText primary="Tracking Summary" primaryTypographyProps={listItemTypography} />}
              </ListItemButton>
            )}

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/work-summary`} selected={isActive('work-summary')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AssessmentIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Work Summary" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            {/* Same list as ClientsListPage's NEW_UI_TEAMSPACES. IN joined 2026-09-15: its
                GST fields (with_gst / gst_type) are only editable on this page. */}
            {['JP', 'GVH', 'TH', 'IN', 'PH', 'DCC'].includes(teamspaceIdToNameMap[selectedTeamspaceId]) && (
              <ListItemButton component={Link} to={`/${selectedTeamspaceId}/clients`} selected={isActive('clients')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BusinessIcon fontSize={iconFontSize} />
                </ListItemIcon>
                {showText && <ListItemText primary="Clients" primaryTypographyProps={listItemTypography} />}
              </ListItemButton>
            )}

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/projects`} selected={isActive('projects')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FolderIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Projects" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/members`} selected={isActive('members')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PeopleIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Members" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/calendar`} selected={isActive('calendar')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CalendarTodayIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Calendar" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/time_entries`} selected={isActive('time_entries')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AccessTimeIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Time Entries" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/tags`} selected={isActive('tags')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LocalOfferIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Tags" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>

            {SALES_DASHBOARD_ENABLED_REGIONS.includes(teamspaceIdToNameMap[selectedTeamspaceId]) && (
              <ListItemButton component={Link} to={`/${selectedTeamspaceId}/sales`} selected={isActive('sales')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ShowChartIcon fontSize={iconFontSize} />
                </ListItemIcon>
                {showText && <ListItemText primary="Sales" primaryTypographyProps={listItemTypography} />}
              </ListItemButton>
            )}

            {canSeeInvoicing && (
              <ListItemButton component={Link} to={`/${selectedTeamspaceId}/invoicing`} selected={isActive('invoicing')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ReceiptLongIcon fontSize={iconFontSize} />
                </ListItemIcon>
                {showText && (
                  <ListItemText
                    primaryTypographyProps={{ ...listItemTypography, component: 'span', sx: { display: 'flex', alignItems: 'center', gap: 0.75 } }}
                    primary={
                      <>
                        Invoicing
                        {showInvoicingDot && (
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', display: 'inline-block', flexShrink: 0 }} />
                        )}
                      </>
                    }
                  />
                )}
              </ListItemButton>
            )}

            {canSeeInvoicing && (
              <ListItemButton component={Link} to={`/${selectedTeamspaceId}/quotation`} selected={isActive('quotation')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <RequestQuoteIcon fontSize={iconFontSize} />
                </ListItemIcon>
                {showText && <ListItemText primary="Quotation" primaryTypographyProps={listItemTypography} />}
              </ListItemButton>
            )}

          {isDeveloper && (
            <ListItemButton component={Link} to={`/${selectedTeamspaceId}/developer-admin`} selected={isActive('developer-admin')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
              <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
                <AdminPanelSettingsIcon fontSize={iconFontSize} />
              </ListItemIcon>
              {showText && <ListItemText primary="Dev Admin" primaryTypographyProps={listItemTypography} />}
            </ListItemButton>
          )}

            {/* <ListItemButton component={Link} to={`/${selectedTeamspaceId}/discounts`}>
              <ListItemIcon>
                <PercentIcon />
              </ListItemIcon>
              {showText && <ListItemText primary="Discounts" />}
            </ListItemButton> */}

            </List>
          </Box>

        {/* Bottom actions pinned to the bottom */}
        <List dense sx={{ mt: '1', pt: 0, display: 'flex', flexDirection: 'column' }}>
          <ListItemButton component={Link} to={`/${selectedTeamspaceId}/profile`} selected={isActive('profile')} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AccountCircleIcon fontSize={iconFontSize} />
            </ListItemIcon>
            {showText && <ListItemText primary="Profile" primaryTypographyProps={listItemTypography} />}
          </ListItemButton>
          <ListItemButton onClick={toggleColorMode} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              {mode === 'dark' ? <LightModeIcon fontSize={iconFontSize} /> : <DarkModeIcon fontSize={iconFontSize} />}
            </ListItemIcon>
            {showText && <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} primaryTypographyProps={listItemTypography} />}
          </ListItemButton>
          <ListItemButton onClick={logoutUser} sx={{ minHeight: itemMinHeight, px: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon fontSize={iconFontSize} />
            </ListItemIcon>
            {showText && <ListItemText primary="Logout" primaryTypographyProps={listItemTypography} />}
          </ListItemButton>
        </List>
          
          {/* Add resize handle (only on desktop) */}
          {!isMobile && (
            <ResizeHandle 
              onMouseDown={handleMouseDown} 
              isResizing={isResizing} 
            />
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;